/**
 * A table that behaves like a spreadsheet, for any list of rows.
 *
 * The CRM tables were each written by hand, each with its own idea of what
 * a header does, and none of them could hold a filter per column, keep a
 * column in view, or remember that somebody had dragged the Company column
 * wider. This is one table they can all be: TanStack Table does the row
 * work (filter, sort, page) and this file draws it in the app's own table
 * styles, so it reads as the tables around it rather than a library's.
 *
 *   - The heading row and the filter row under it stay put while the body
 *     scrolls, inside the grid's own scroller.
 *   - The first N visible columns (0-4, picked from the toolbar) stay put
 *     while the rest scroll sideways; a column with `meta.pin: 'right'` - a
 *     row's buttons - stays against the right edge.
 *   - Columns are dragged into order by their headings, widened by the
 *     handle on their right edge, and shown or hidden from the Columns menu.
 *   - That layout (order, hidden, widths, frozen count, sort, page size,
 *     density) is saved per person - see useGridPrefs - and restored on any
 *     device. Filters and the search are not: they are today's question.
 *   - Every column has a filter suited to `meta.type`, and the toolbar
 *     searches every visible column at once.
 *   - Rows are paged, 25 to start with, and the Download menu takes all of
 *     them - every page, as filtered and sorted - not the page on screen.
 *
 * ## Columns
 *
 * TanStack column definitions, each with an `id` and an `accessorFn` that
 * returns the raw value (undefined for nothing), plus:
 *
 *   meta.label        the heading, the menu entry and the file's column name
 *   meta.type         'text' | 'enum' | 'number' | 'date' (default 'text')
 *   meta.options      enum: the values in the order to list them, as strings
 *                     or [value, label] pairs; others present are added
 *   meta.blank        what nothing is called ("Unowned"), for filters and search
 *   meta.exportValue  (row) => the plain value for the file and the search
 *   meta.exportType   the type it goes into a file as, if not meta.type
 *   meta.searchValue  (row) => the text the search reads, if not exportValue
 *   meta.filter       false to have no filter; meta.search false to be skipped
 *   meta.export       false to leave the column out of downloads
 *   meta.pin          'right' for the actions column
 *
 * `cell` draws the cell as usual. Keep `columns` and `defaults` stable
 * (module constants or useMemo): the table re-derives everything from them.
 */
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  useReactTable, getCoreRowModel, getFilteredRowModel, getPaginationRowModel,
  getFacetedRowModel, flexRender, functionalUpdate
} from '@tanstack/react-table';
import { useApp } from '../../store/AppContext.jsx';
import Icon from '../../lib/icons.jsx';
import { formatDate } from '../../lib/dates.js';
import { isOffline } from '../../lib/storage.js';
import { EmptyState, Dash } from '../Primitives.jsx';
import useGridPrefs from './useGridPrefs.js';
import ColumnFilter from './ColumnFilter.jsx';
import { ColumnsMenu, FreezeMenu, DownloadMenu } from './GridMenus.jsx';
import { zoomOf } from './Popover.jsx';
import {
  resolveLayout, toSaved, moveColumn, nudgeColumn, defaultWidth, isPinnedRight,
  LAYOUT_KEYS, PAGE_SIZES, MIN_WIDTH, MAX_WIDTH
} from './layout.js';
import {
  filterFor, compareFor, sortedRowModel, textOfValue, exportCell, asDate, asNumber, isBlank
} from './filters.js';
import { exportView, exportType } from './exportGrid.js';

const SEARCH_DELAY = 200;
/* A frozen block may take this much of the width the table has to scroll
   in; past it, fewer columns are held than were asked for. */
const FROZEN_SHARE = 0.65;
const RESIZE_STEP = 16;

const typeOf = def => (def.meta && def.meta.type) || 'text';
const labelOf = def => (def.meta && def.meta.label) || (typeof def.header === 'string' ? def.header : def.id);
/* Column ids go into CSS custom property names, which take a narrow alphabet. */
const cssId = id => String(id).replace(/[^a-zA-Z0-9_-]/g, '_');

/** A cell for a column that did not bring its own renderer. */
function plainCell(type, value, dateFmt) {
  if (isBlank(value)) return <Dash />;
  if (type === 'date') return formatDate(asDate(value), false, dateFmt) || <Dash />;
  if (type === 'number') return asNumber(value) === null ? <Dash /> : asNumber(value).toLocaleString('en-US');
  return String(value);
}

export default function DataGrid({
  columns,
  data,
  getRowId,
  prefKey,
  defaults,
  prefs: handed,
  onRowClick,
  toolbar = null,
  viewKey,
  exportName = 'export',
  exportSheet,
  canExport = true,
  dateFmt = 'long',
  emptyState = null,
  label
}) {
  const { toast } = useApp();

  /* A page that keeps its own state in the same saved layout (the pipeline
     keeps its stage tab there) hands its useGridPrefs in; otherwise the
     grid keeps one of its own under `prefKey`. */
  const own = useGridPrefs(handed ? null : prefKey, defaults);
  const store = handed || own;
  const rawBase = store.defaults || defaults || {};
  const baseKey = JSON.stringify(rawBase);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const base = useMemo(() => rawBase, [baseKey]);

  const byId = useMemo(() => new Map(columns.map(c => [c.id, c])), [columns]);
  const pinnedRight = useMemo(() => columns.filter(isPinnedRight).map(c => c.id), [columns]);
  const layout = useMemo(() => resolveLayout(columns, store.prefs, base), [columns, store.prefs, base]);
  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  /*
   * Every change to the layout goes through here: worked out against the
   * latest saved value rather than this render's, so two quick changes (a
   * drag moves sixty times a second) cannot overwrite each other, and
   * stored with whatever it says about columns this table does not have.
   */
  const argsRef = useRef({ columns, base });
  argsRef.current = { columns, base };
  const setPrefs = store.set;
  const commit = useCallback(change => {
    setPrefs(prev => {
      const { columns: cols, base: b } = argsRef.current;
      const current = resolveLayout(cols, prev, b);
      const next = { ...current, ...change(current) };
      return { ...prev, ...toSaved(next, prev, cols, b) };
    });
  }, [setPrefs]);

  const visible = layout.order.filter(id => !layout.hidden.has(id));

  /* ------------------------------------------------------------ search */

  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchText.trim()), SEARCH_DELAY);
    return () => clearTimeout(t);
  }, [searchText]);

  /* How each column reads a row: its value, its words, and its file cell. */
  const readers = useMemo(() => {
    const m = new Map();
    columns.forEach(c => {
      const meta = c.meta || {};
      const type = typeOf(c);
      const value = c.accessorFn
        ? r => c.accessorFn(r, 0)
        : c.accessorKey ? r => r[c.accessorKey] : () => undefined;
      const plain = r => (meta.exportValue ? meta.exportValue(r) : value(r));
      /* A column can sort as one thing and export as another: the last
         activity sorts by its date but goes into a file as what was done. */
      const outType = meta.exportType || type;
      m.set(c.id, {
        value,
        text: meta.searchValue
          ? r => String(meta.searchValue(r) ?? '')
          : r => textOfValue(outType, plain(r), { dateFmt, blank: meta.blank || '' }),
        out: r => exportCell(outType, plain(r))
      });
    });
    return m;
  }, [columns, dateFmt]);

  /*
   * The search reads the visible columns only - what somebody searches is
   * what they can see - and every word has to be found somewhere in the
   * row, so "acme dubai" narrows rather than widens.
   */
  const searchable = visible.filter(id => (byId.get(id).meta || {}).search !== false);
  const searchKey = searchable.join('|');
  const searched = useMemo(() => {
    const words = search.toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return data;
    const texts = searchable.map(id => readers.get(id).text);
    return data.filter(r => {
      const hay = texts.map(t => t(r)).join('\n').toLowerCase();
      return words.every(w => hay.includes(w));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, search, searchKey, readers]);

  /* ----------------------------------------------------------- columns */

  const tableColumns = useMemo(() => columns.map(c => {
    const type = typeOf(c);
    const reader = readers.get(c.id);
    const cmp = compareFor(type);
    const pinned = isPinnedRight(c);
    return {
      ...c,
      accessorFn: c.accessorFn || reader.value,
      enableSorting: !pinned && c.enableSorting !== false,
      enableColumnFilter: !pinned && (c.meta || {}).filter !== false,
      enableHiding: !pinned,
      filterFn: c.filterFn || filterFor(type, reader.text),
      sortingFn: c.sortingFn || ((a, b, id) => cmp(a.getValue(id), b.getValue(id))),
      cell: c.cell || (ctx => plainCell(type, ctx.getValue(), dateFmt))
    };
  }), [columns, readers, dateFmt]);

  /* -------------------------------------------------------- frozen part */

  /* How wide the scroller is, in the same page pixels as the columns. */
  const scroller = useRef(null);
  const [viewW, setViewW] = useState(0);
  useEffect(() => {
    const el = scroller.current;
    if (!el) return undefined;
    const measure = () => setViewW(el.clientWidth);
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const rightWidth = pinnedRight.reduce((t, id) => t + layout.sizes[id], 0);
  let fits = Math.min(layout.frozen, visible.length);
  if (viewW) {
    let used = 0;
    let n = 0;
    while (n < fits && used + layout.sizes[visible[n]] <= (viewW - rightWidth) * FROZEN_SHARE) {
      used += layout.sizes[visible[n]];
      n += 1;
    }
    fits = n;
  }
  const frozenIds = visible.slice(0, fits);

  /* ------------------------------------------------------------- table */

  /*
   * State handed to TanStack is memoised on its content, not its identity.
   * The layout is re-read on every change - a column being dragged wider
   * changes it sixty times a second - and a fresh sort array each time
   * would re-sort every row and redraw the whole body for a width.
   */
  const orderKey = [...layout.order, ...pinnedRight].join('|');
  const hiddenKey = layout.order.filter(id => layout.hidden.has(id)).join('|');
  const sortKey = layout.sort.map(s => `${s.id}:${s.desc ? 'd' : 'a'}`).join('|');
  const pinKey = `${frozenIds.join('|')}#${pinnedRight.join('|')}`;
  /* eslint-disable react-hooks/exhaustive-deps */
  const columnOrder = useMemo(() => [...layout.order, ...pinnedRight], [orderKey]);
  const columnVisibility = useMemo(() => Object.fromEntries(columns.map(c => [c.id, !layout.hidden.has(c.id)])), [hiddenKey, columns]);
  const sorting = useMemo(() => layout.sort, [sortKey]);
  const columnPinning = useMemo(() => ({ left: frozenIds, right: pinnedRight }), [pinKey]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const [filters, setFilters] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = layout.pageSize;
  const pagination = useMemo(() => ({ pageIndex, pageSize }), [pageIndex, pageSize]);

  const table = useReactTable({
    data: searched,
    columns: tableColumns,
    getRowId,
    state: {
      columnOrder,
      columnVisibility,
      columnSizing: layout.sizes,
      columnPinning,
      sorting,
      columnFilters: filters,
      pagination
    },
    onSortingChange: updater => commit(l => ({ sort: functionalUpdate(updater, l.sort) })),
    onColumnFiltersChange: updater => setFilters(old => functionalUpdate(updater, old)),
    onPaginationChange: updater => {
      const next = functionalUpdate(updater, pagination);
      setPageIndex(next.pageIndex);
      if (next.pageSize !== pageSize) commit(() => ({ pageSize: next.pageSize }));
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: sortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    /* Paging is reset here, on purpose: TanStack's own reset also fires when
       the rows change, so saving a lead on page 3 would throw you back to 1. */
    autoResetPageIndex: false,
    enableMultiSort: true,
    isMultiSortEvent: e => !!(e && e.shiftKey),
    /* Ascending first on every column, numbers and dates included. */
    defaultColumn: { sortDescFirst: false, minSize: MIN_WIDTH, maxSize: MAX_WIDTH }
  });

  const total = table.getPrePaginationRowModel().rows.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const narrowed = filters.length > 0 || !!search;

  /* A new question starts at the top. */
  useEffect(() => { setPageIndex(0); }, [filters, search, sortKey, viewKey, pageSize]);
  /* Fewer rows than the page being shown (a filter, a deletion): the last
     page, before the empty one is ever painted. */
  useLayoutEffect(() => {
    if (pageIndex > pageCount - 1) setPageIndex(pageCount - 1);
  }, [pageIndex, pageCount]);

  const rows = table.getRowModel().rows;

  /* --------------------------------------------------- header geometry */

  /* The filter row sticks under the heading row, so it needs that row's
     height as its offset. Measured, because a dozen stylesheets have a say
     in how tall a heading is. */
  const headRow = useRef(null);
  const [headH, setHeadH] = useState(0);
  useLayoutEffect(() => {
    const h = headRow.current ? headRow.current.offsetHeight : 0;
    if (h && h !== headH) setHeadH(h);
  });

  /* The shadows on the frozen edges, only while something is under them. */
  const edges = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    const left = el.scrollLeft > 0;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    if (el.dataset.sl !== String(left)) el.dataset.sl = String(left);
    if (el.dataset.sr !== String(right)) el.dataset.sr = String(right);
  }, []);
  useEffect(edges);

  const vars = { '--dg-h1': headH ? `${headH}px` : undefined };
  let width = 0;
  [...visible, ...pinnedRight].forEach(id => {
    vars[`--dg-w-${cssId(id)}`] = `${layout.sizes[id]}px`;
    width += layout.sizes[id];
  });
  frozenIds.forEach(id => { vars[`--dg-l-${cssId(id)}`] = `${table.getColumn(id).getStart('left')}px`; });
  pinnedRight.forEach(id => { vars[`--dg-r-${cssId(id)}`] = `${table.getColumn(id).getAfter('right')}px`; });

  /* ------------------------------------------------------------ resize */

  const resizingRef = useRef(null);
  const [resizing, setResizing] = useState(null);

  const setWidth = (id, w) => {
    const def = byId.get(id);
    const clamped = Math.round(Math.min(def.maxSize ?? MAX_WIDTH, Math.max(def.minSize ?? MIN_WIDTH, w)));
    commit(l => ({ sizes: { ...l.sizes, [id]: clamped } }));
  };

  /*
   * Dragging the handle. The pointer moves in screen pixels and a column is
   * sized in page pixels, which differ by the interface zoom, so the
   * distance is divided by it - otherwise the edge trails the pointer.
   */
  const startResize = (e, id) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const handle = e.currentTarget;
    const z = zoomOf(handle.parentElement);
    const startX = e.clientX;
    const startW = layoutRef.current.sizes[id];
    let last = startW;
    resizingRef.current = id;
    setResizing(id);
    try { handle.setPointerCapture(e.pointerId); } catch { /* not a real pointer */ }
    const move = ev => {
      const w = Math.round(startW + (ev.clientX - startX) / z);
      if (w !== last) { last = w; setWidth(id, w); }
    };
    const end = () => {
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', end);
      handle.removeEventListener('pointercancel', end);
      resizingRef.current = null;
      setResizing(null);
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
  };

  const resizeKey = (e, id) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    e.stopPropagation();
    const step = (e.shiftKey ? 4 : 1) * RESIZE_STEP * (e.key === 'ArrowLeft' ? -1 : 1);
    setWidth(id, layoutRef.current.sizes[id] + step);
  };

  /* ----------------------------------------------------------- reorder */

  const dragRef = useRef(null);
  const [dragging, setDragging] = useState(null);
  const [dropAt, setDropAt] = useState(null);

  const endDrag = () => { dragRef.current = null; setDragging(null); setDropAt(null); };

  const dragStart = (e, id) => {
    /* A drag that began on the resize handle is a resize, not a move. */
    if (resizingRef.current) { e.preventDefault(); return; }
    dragRef.current = id;
    setDragging(id);
    e.dataTransfer.effectAllowed = 'move';
    /* Its own type, so dropping a heading on the search box types nothing. */
    try { e.dataTransfer.setData('application/x-grid-column', id); } catch { /* old browser */ }
  };

  const dragOver = (e, id) => {
    const from = dragRef.current;
    if (!from) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (from === id) { if (dropAt) setDropAt(null); return; }
    const r = e.currentTarget.getBoundingClientRect();
    const side = e.clientX < r.left + r.width / 2 ? 'before' : 'after';
    if (!dropAt || dropAt.id !== id || dropAt.side !== side) setDropAt({ id, side });
  };

  const dropOn = (e, id) => {
    e.preventDefault();
    const from = dragRef.current;
    const side = dropAt && dropAt.id === id ? dropAt.side : 'before';
    endDrag();
    if (from && from !== id) commit(l => ({ order: moveColumn(l.order, from, id, side) }));
  };

  /* ------------------------------------------------------------- menus */

  /* Hiding a column takes its filter and its sort with it: rows narrowed or
     ordered by something no longer on screen is a table that cannot say
     why it looks the way it does. */
  const toggleColumn = id => {
    const hiding = !layoutRef.current.hidden.has(id);
    if (hiding) setFilters(old => old.filter(f => f.id !== id));
    commit(l => {
      const hidden = new Set(l.hidden);
      if (hidden.has(id)) hidden.delete(id);
      else if (l.order.filter(x => !hidden.has(x)).length > 1) hidden.add(id);
      return { hidden, sort: hidden.has(id) ? l.sort.filter(s => s.id !== id) : l.sort };
    });
  };
  const nudge = (id, step) => commit(l => ({ order: nudgeColumn(l.order, l.hidden, id, step) }));
  const showAll = () => commit(() => ({ hidden: new Set() }));
  const resetLayout = () => store.reset(LAYOUT_KEYS);
  const freeze = n => commit(() => ({ frozen: n }));
  const compact = layout.density === 'compact';
  const toggleDensity = () => commit(l => ({ density: l.density === 'compact' ? 'comfortable' : 'compact' }));

  const clearFilters = () => { setFilters([]); setSearchText(''); setSearch(''); };

  /* ---------------------------------------------------------- download */

  const [exporting, setExporting] = useState(null);
  const download = async format => {
    if (exporting) return;
    const ids = visible.filter(id => (byId.get(id).meta || {}).export !== false);
    const cols = ids.map(id => {
      const def = byId.get(id);
      return { key: id, label: labelOf(def), type: exportType((def.meta || {}).exportType || typeOf(def)) };
    });
    const out = table.getPrePaginationRowModel().rows.map(r => {
      const o = {};
      ids.forEach(id => { o[id] = readers.get(id).out(r.original); });
      return o;
    });
    setExporting(format);
    try {
      await exportView({ format, name: exportName, sheet: exportSheet, columns: cols, rows: out });
      toast(`Exported ${out.length} row${out.length === 1 ? '' : 's'}`);
    } catch (e) {
      toast(e.message || 'That export could not be made');
    } finally {
      setExporting(null);
    }
  };

  /* -------------------------------------------------------------- rows */

  const clickRef = useRef(onRowClick);
  clickRef.current = onRowClick;
  const openRow = useCallback((e, row) => {
    if (!clickRef.current) return;
    /* A link, a button or a chip in the cell does its own thing; the
       actions cell is marked so a miss between its buttons does nothing. */
    if (e.target.closest && e.target.closest('a, button, input, select, textarea, label, [data-grid-stop]')) return;
    clickRef.current(row.original);
  }, []);
  const rowKey = useCallback((e, row) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (clickRef.current) clickRef.current(row.original);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next = e.key === 'ArrowDown' ? e.currentTarget.nextElementSibling : e.currentTarget.previousElementSibling;
      if (next) next.focus();
    }
  }, []);

  /* ---------------------------------------------------------- drawing */

  const pinClass = id => {
    if (frozenIds.includes(id)) {
      return ` dg-pin dg-pin-l${id === frozenIds[frozenIds.length - 1] ? ' dg-edge-l' : ''}`;
    }
    if (pinnedRight.includes(id)) return ` dg-pin dg-pin-r${id === pinnedRight[0] ? ' dg-edge-r' : ''}`;
    return '';
  };
  const pinStyle = id => (frozenIds.includes(id) ? { left: `var(--dg-l-${cssId(id)})` }
    : pinnedRight.includes(id) ? { right: `var(--dg-r-${cssId(id)})` } : undefined);

  const sortIndex = new Map(layout.sort.map((s, i) => [s.id, i]));
  const many = layout.sort.length > 1;
  const from = total ? pageIndex * pageSize + 1 : 0;
  const to = Math.min(total, (pageIndex + 1) * pageSize);
  const offline = isOffline();

  return (
    <div className={`dg${compact ? ' dg-compact' : ''}${resizing ? ' dg-resizing' : ''}${dragging ? ' dg-dragging' : ''}`}>
      <div className="dg-bar">
        {toolbar ? <div className="dg-slot">{toolbar}</div> : null}
        <div className="dg-tools">
          <div className="srch dg-search">
            <Icon name="search" />
            <input
              value={searchText}
              placeholder="Search every column…"
              aria-label="Search every visible column"
              onChange={e => setSearchText(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Escape' && searchText) { e.stopPropagation(); setSearchText(''); setSearch(''); }
              }}
            />
            {searchText ? (
              <button
                type="button"
                className="iconbtn sm"
                aria-label="Clear the search"
                onClick={() => { setSearchText(''); setSearch(''); }}
              >
                <Icon name="x" />
              </button>
            ) : null}
          </div>
          {narrowed ? (
            <button type="button" className="btn sm" onClick={clearFilters} title="Clear every column filter and the search">
              <Icon name="filter" />Clear filters
              <span className="fcount">{filters.length + (search ? 1 : 0)}</span>
            </button>
          ) : null}
          <ColumnsMenu
            columns={columns.filter(c => !isPinnedRight(c))}
            order={layout.order}
            hidden={layout.hidden}
            frozenIds={frozenIds}
            onToggle={toggleColumn}
            onMove={nudge}
            onShowAll={showAll}
            onReset={resetLayout}
          />
          <FreezeMenu
            frozen={layout.frozen}
            fits={fits}
            max={visible.length}
            names={visible.map(id => labelOf(byId.get(id)))}
            onPick={freeze}
          />
          <button
            type="button"
            className={`btn sm${compact ? ' dg-lit' : ''}`}
            aria-pressed={compact}
            title={compact ? 'Back to roomier rows' : 'Tighter rows, more of them on screen'}
            onClick={toggleDensity}
          >
            <Icon name="menu" />Compact
          </button>
          {canExport ? (
            <DownloadMenu busy={!!exporting} count={total} offline={offline} onPick={download} />
          ) : null}
        </div>
      </div>

      <div className="dg-scroll" ref={scroller} onScroll={edges}>
        <table
          className="dg-t"
          aria-label={label}
          style={{ ...vars, width: `max(100%, ${width}px)` }}
        >
          <colgroup>
            {visible.map(id => <col key={id} style={{ width: `var(--dg-w-${cssId(id)})` }} />)}
            <col />
            {pinnedRight.map(id => <col key={id} style={{ width: `var(--dg-w-${cssId(id)})` }} />)}
          </colgroup>

          <thead>
            <tr ref={headRow} className="dg-hrow">
              {visible.map(id => {
                const column = table.getColumn(id);
                const def = byId.get(id);
                const name = labelOf(def);
                const sortable = column.getCanSort();
                const dir = column.getIsSorted();
                const at = sortIndex.get(id);
                const drop = dropAt && dropAt.id === id ? ` dg-drop-${dropAt.side}` : '';
                return (
                  <th
                    key={id}
                    scope="col"
                    className={`dg-th${dir ? ' sorted' : ''}${pinClass(id)}${drop}${dragging === id ? ' dg-moving' : ''}`}
                    style={pinStyle(id)}
                    aria-sort={sortable ? (dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none') : undefined}
                    tabIndex={sortable ? 0 : undefined}
                    title={sortable ? `Sort by ${name} — shift-click to sort by more than one column. Drag to move.` : 'Drag to move'}
                    draggable
                    onClick={sortable ? column.getToggleSortingHandler() : undefined}
                    onKeyDown={sortable ? e => {
                      if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return;
                      e.preventDefault();
                      column.toggleSorting(undefined, e.shiftKey);
                    } : undefined}
                    onDragStart={e => dragStart(e, id)}
                    onDragOver={e => dragOver(e, id)}
                    onDrop={e => dropOn(e, id)}
                    onDragEnd={endDrag}
                  >
                    <span className="dg-lab">{name}</span>
                    {/* The arrow and the sort's place are drawn by CSS from
                        these attributes, so the heading's text stays its name. */}
                    <span
                      className="dg-ar"
                      data-dir={dir || undefined}
                      data-ord={dir && many ? at + 1 : undefined}
                      aria-hidden="true"
                    />
                    <span
                      className="dg-rsz"
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={`Resize ${name}`}
                      aria-valuenow={Math.round(layout.sizes[id])}
                      aria-valuemin={def.minSize ?? MIN_WIDTH}
                      aria-valuemax={def.maxSize ?? MAX_WIDTH}
                      tabIndex={0}
                      title="Drag to resize; double-click for the default width"
                      onPointerDown={e => startResize(e, id)}
                      onClick={e => e.stopPropagation()}
                      onDoubleClick={e => { e.stopPropagation(); setWidth(id, defaultWidth(def, base)); }}
                      onKeyDown={e => resizeKey(e, id)}
                    />
                  </th>
                );
              })}
              <th className="dg-fill" aria-hidden="true" />
              {pinnedRight.map(id => (
                <th key={id} scope="col" className={`dg-th${pinClass(id)}`} style={pinStyle(id)}>
                  <span className="dg-sr">{labelOf(byId.get(id))}</span>
                </th>
              ))}
            </tr>

            <tr className="dg-frow">
              {visible.map(id => {
                const column = table.getColumn(id);
                const def = byId.get(id);
                return (
                  <th key={id} className={`dg-fth${pinClass(id)}`} style={pinStyle(id)}>
                    {column.getCanFilter() ? (
                      <ColumnFilter
                        column={column}
                        type={typeOf(def)}
                        meta={{ ...(def.meta || {}), label: labelOf(def) }}
                        dateFmt={dateFmt}
                      />
                    ) : null}
                  </th>
                );
              })}
              <th className="dg-fill dg-fth" aria-hidden="true" />
              {pinnedRight.map(id => <th key={id} className={`dg-fth${pinClass(id)}`} style={pinStyle(id)} />)}
            </tr>
          </thead>

          <GridBody
            rows={rows}
            columns={tableColumns}
            cellsKey={`${visible.join('|')}#${pinKey}`}
            pinClass={pinClass}
            pinStyle={pinStyle}
            clickable={!!onRowClick}
            openRow={openRow}
            rowKey={rowKey}
          />
        </table>
      </div>

      {!total ? (
        narrowed ? (
          <EmptyState icon="filter" tone="g-slate" title="No rows match">
            <div className="dg-emptyacts">
              Nothing here matches the filters{search ? ' and the search' : ''}.
              <button type="button" className="btn sm" onClick={clearFilters}>
                <Icon name="refresh" />Clear filters
              </button>
            </div>
          </EmptyState>
        ) : emptyState || (
          <EmptyState icon="search" tone="g-slate" title="Nothing here yet">No rows to show.</EmptyState>
        )
      ) : null}

      <div className="dg-foot">
        <span className="dg-count" aria-live="polite">
          {total ? `${from.toLocaleString('en-US')}–${to.toLocaleString('en-US')} of ${total.toLocaleString('en-US')}` : 'No rows'}
          {narrowed ? <small> · filtered from {data.length.toLocaleString('en-US')}</small> : null}
        </span>
        <div className="dg-pager">
          <label className="dg-psize">
            Rows per page
            <select
              value={pageSize}
              onChange={e => commit(() => ({ pageSize: Number(e.target.value) }))}
            >
              {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <button type="button" className="dg-pg dg-first" aria-label="First page" title="First page"
            disabled={pageIndex === 0} onClick={() => setPageIndex(0)}>
            <Icon name="chev" /><Icon name="chev" />
          </button>
          <button type="button" className="dg-pg dg-prev" aria-label="Previous page" title="Previous page"
            disabled={pageIndex === 0} onClick={() => setPageIndex(i => Math.max(0, i - 1))}>
            <Icon name="chev" />
          </button>
          <span className="dg-pgn">Page {Math.min(pageIndex + 1, pageCount)} of {pageCount}</span>
          <button type="button" className="dg-pg dg-next" aria-label="Next page" title="Next page"
            disabled={pageIndex >= pageCount - 1} onClick={() => setPageIndex(i => Math.min(pageCount - 1, i + 1))}>
            <Icon name="chev" />
          </button>
          <button type="button" className="dg-pg dg-last" aria-label="Last page" title="Last page"
            disabled={pageIndex >= pageCount - 1} onClick={() => setPageIndex(pageCount - 1)}>
            <Icon name="chev" /><Icon name="chev" />
          </button>
        </div>
      </div>
    </div>
  );
}

/*
 * The rows, kept apart so they are only redrawn when something about the
 * rows changes. Widening a column changes the widths and nothing else, and
 * those live in CSS variables on the table, so 250 rows of chips are not
 * redrawn sixty times a second while a handle is dragged.
 */
const GridBody = memo(function GridBody({ rows, columns, cellsKey, pinClass, pinStyle, clickable, openRow, rowKey }) {
  return (
    <tbody>
      {rows.map(row => {
        const cells = [...row.getLeftVisibleCells(), ...row.getCenterVisibleCells()];
        return (
          <tr
            key={row.id}
            className={`dg-row${clickable ? ' dg-link' : ''}`}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? e => openRow(e, row) : undefined}
            onKeyDown={clickable ? e => rowKey(e, row) : undefined}
          >
            {/* The content is clipped by an inner box rather than by the cell,
                so the shadow a frozen cell casts past its edge is not. */}
            {cells.map(cell => (
              <td key={cell.id} className={`dg-td${pinClass(cell.column.id)}`} style={pinStyle(cell.column.id)}>
                <div className="dg-cell">{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
              </td>
            ))}
            <td className="dg-fill" aria-hidden="true" />
            {row.getRightVisibleCells().map(cell => (
              <td
                key={cell.id}
                className={`dg-td${pinClass(cell.column.id)}`}
                style={pinStyle(cell.column.id)}
                data-grid-stop=""
                onClick={e => e.stopPropagation()}
              >
                <div className="dg-cell">{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
              </td>
            ))}
          </tr>
        );
      })}
    </tbody>
  );
}, (a, b) => a.rows === b.rows && a.columns === b.columns && a.cellsKey === b.cellsKey
  && a.clickable === b.clickable);
